import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import defaultContent from "../../../../content/performanceIncomeDashboard";
import defaultAwardContent from "../../../../content/awardLabels";
import { LeafPerfBonusScale } from "../../../../common/leaf/LeafPerfBonusScale/LeafPerfBonusScale";
import { SopCard, SopCardProps } from "./SOP/SopCard";
import type { PerfBonusScaleData } from "./PerformanceBonusTile.mask";
import type { HeaderVariant } from "../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI.types";
import { maskPerfBonusTileData } from "./PerformanceBonusTile.mask";
import { useAppSelector } from "../../../../store";
import "./PerformanceBonusTile.scss";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";

interface Props {
  totalGpv: string; // Formatted texti
  performanceBonusPercent: string;
  sopData?: SopCardProps | null;
  perfBonusScaleData: PerfBonusScaleData;
  qualifiedLegs: string;
  isSpQualified: boolean;
  headerVariant: HeaderVariant;
  gpvNeeded: string;
  percentNeeded: string;
  className?: string;
  isLoading?: boolean;
}

const Loader = () => {
  return (
    <div className={`PerformanceBonusTile`} data-testid="perf-bonus-tile-loading">
      <div className="PerformanceBonusTile__header">
        <LeafRectangleLoader isLoading styleClass="PerformanceBonusTile__loader--title" />
      </div>
      <div className="PerformanceBonusTile__content">
        <div className="PerformanceBonusTile__summary">
          <div className="PerformanceBonusTile__summary-item">
            <div className="PerformanceBonusTile__summary-label">
              <LeafRectangleLoader isLoading styleClass="PerformanceBonusTile__loader--summaryLabel" />
            </div>
            <div className="PerformanceBonusTile__summary-value">
              <LeafRectangleLoader isLoading styleClass="PerformanceBonusTile__loader--summaryValue" />
            </div>
          </div>

          <div className="PerformanceBonusTile__summary-item PerformanceBonusTile__summary-item--right">
            <div className="PerformanceBonusTile__summary-label">
              <LeafRectangleLoader
                isLoading
                styleClass="PerformanceBonusTile__loader--summaryLabel PerformanceBonusTile__loader--right"
              />
            </div>
            <div className="PerformanceBonusTile__summary-value">
              <LeafRectangleLoader
                isLoading
                styleClass="PerformanceBonusTile__loader--summaryValue PerformanceBonusTile__loader--right"
              />
            </div>
          </div>
        </div>

        <div className="PerformanceBonusTile__scale">
          <LeafRectangleLoader isLoading height=".75rem" width="100%" margin="3rem 0 1.5rem 0" />
          <div style={{ margin: "auto", width: "min-content" }}>
            <LeafRectangleLoader isLoading height="1.5rem" width="16rem" margin="0" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const PerformanceBonusTile = (props: Props) => {
  const { sopData, className = "", isLoading = false } = props;
  const { t } = useTranslation(["performanceIncomeDashboard", "awardLabels"]);
  const showData = useAppSelector((state) => state.showData.showData);

  if (isLoading) return <Loader />;

  const noDataLabel = t(
    "awardLabels.no-data-available",
    defaultAwardContent.awardLabels["no-data-available"] as string,
  );

  const {
    totalGpv,
    performanceBonusPercent,
    qualifiedLegs,
    headerVariant,
    gpvNeeded,
    percentNeeded,
    isSpQualified,
    scaleData: perfBonusScaleData,
  } = maskPerfBonusTileData(
    {
      totalGpv: props.totalGpv,
      performanceBonusPercent: props.performanceBonusPercent,
      qualifiedLegs: props.qualifiedLegs,
      isSpQualified: props.isSpQualified,
      headerVariant: props.headerVariant,
      gpvNeeded: props.gpvNeeded,
      percentNeeded: props.percentNeeded,
      scaleData: props.perfBonusScaleData,
    },
    showData,
  );

  const hasQualifiedLegs = qualifiedLegs && parseInt(qualifiedLegs) > 0;

  const hasHeaderVariant = headerVariant !== "";
  const headerI18nKey = hasHeaderVariant ? `performance.incomeTile.title.${headerVariant}` : "";
  const headerDefault = hasHeaderVariant
    ? ((defaultContent.performance.incomeTile.title as Record<string, string>)[headerVariant] ?? "")
    : "";
  const headerAriaDefault = headerDefault.replace(/<\/?1>/g, "");
  const headerAriaLabel =
    !showData && hasHeaderVariant
      ? t(headerI18nKey, headerAriaDefault, {
          ns: "performanceIncomeDashboard",
          percent: noDataLabel,
          amount: noDataLabel,
          interpolation: { escapeValue: false },
        }).toString()
      : undefined;

  const renderHeader = () => {
    const showSpQualified = isSpQualified;
    const showGpvNeeded = hasHeaderVariant;

    if (!showSpQualified && !showGpvNeeded) {
      return null;
    }

    return (
      <div
        className={`PerformanceBonusTile__header${showSpQualified && showGpvNeeded ? " PerformanceBonusTile__header--two-line" : ""}`}
      >
        {showSpQualified && (
          <div className="PerformanceBonusTile__header-line PerformanceBonusTile__header-line-bold">
            {t(
              "performance.incomeTile.title.spQualified",
              defaultContent.performance.incomeTile.title.spQualified,
            ).toString()}
          </div>
        )}
        {showGpvNeeded && (
          <div
            className={`PerformanceBonusTile__header-line${showSpQualified ? " PerformanceBonusTile__header-line--subtitle" : ""}`}
            {...(!showData ? { role: "text", "aria-label": headerAriaLabel } : {})}
          >
            <span {...(!showData ? { "aria-hidden": true } : {})}>
              <Trans
                ns="performanceIncomeDashboard"
                i18nKey={headerI18nKey}
                defaults={headerDefault}
                values={{ percent: percentNeeded, amount: gpvNeeded }}
                components={{ 1: <strong /> }}
              />
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`PerformanceBonusTile ${className}`} data-testid="perf-bonus-tile">
      {renderHeader()}

      <div className="PerformanceBonusTile__content">
        <div className="PerformanceBonusTile__summary">
          <div className="PerformanceBonusTile__summary-item">
            <div className="PerformanceBonusTile__summary-label" data-testid="perf-bonus-tile-gpv-label">
              {t("performance.incomeTile.total", defaultContent.performance.incomeTile.total).toString()}
            </div>
            <div
              className="PerformanceBonusTile__summary-value"
              {...(totalGpv === "--" ? { role: "text", "aria-label": noDataLabel } : {})}
            >
              {totalGpv === "--" ? <span aria-hidden="true">{totalGpv}</span> : totalGpv}
            </div>
          </div>
          <div className="PerformanceBonusTile__summary-item PerformanceBonusTile__summary-item--right">
            <div className="PerformanceBonusTile__summary-label" data-testid="perf-bonus-tile-bonus-level-label">
              {t("performance.incomeTile.bonusLevel", defaultContent.performance.incomeTile.bonusLevel).toString()}
            </div>
            <div
              className="PerformanceBonusTile__summary-value"
              {...(performanceBonusPercent === "--" ? { role: "text", "aria-label": noDataLabel } : {})}
            >
              {performanceBonusPercent === "--" ? (
                <span aria-hidden="true">{performanceBonusPercent}</span>
              ) : (
                performanceBonusPercent
              )}
            </div>
          </div>
        </div>

        <div className="PerformanceBonusTile__scale">
          <LeafPerfBonusScale
            max={perfBonusScaleData.max}
            progressValue={perfBonusScaleData.progressValue}
            ticks={perfBonusScaleData.ticks}
            milestones={perfBonusScaleData.milestones}
            legends={perfBonusScaleData.legends}
          />
        </div>

        {sopData && sopData.current && sopData.expected && <hr className="PerformanceBonusTile__divider" />}

        <div className="PerformanceBonusTile__details">
          {sopData && sopData.current && sopData.expected && (
            <>
              <SopCard current={sopData.current} expected={sopData.expected} monthlyPv={sopData.monthlyPv || []} />
              <hr className="PerformanceBonusTile__divider" />
            </>
          )}

          {hasQualifiedLegs && (
            <>
              <hr className="PerformanceBonusTile__qualified-legs-divider" />
              <div className="PerformanceBonusTile__qualified-legs">
                <div className="PerformanceBonusTile__qualified-legs-label">
                  {t(
                    "performance.incomeTile.qualifiedLegs",
                    defaultContent.performance.incomeTile.qualifiedLegs,
                  ).toString()}
                </div>
                <div className="PerformanceBonusTile__qualified-legs-value">{qualifiedLegs}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
